import { keyframes, styled } from "@styles";
import { fadeIn } from "@styles/animations";

export const fadeInFromLeft = keyframes({
  from: {
    opacity: 0,
    transform: "translateX(-20px)",
  },
  to: {
    opacity: 1,
    transform: "translateX(0)",
  },
});

export const fadeInFromRight = keyframes({
  from: {
    opacity: 0,
    transform: "translateX(20px)",
  },
  to: {
    opacity: 1,
    transform: "translateX(0)",
  },
});

export const Container = styled("div", {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gridGap: "60px 32px",

  padding: "116px ",

  width: "100%",
  maxWidth: "1260px",

  ".fade-in": {
    opacity: 0,
    animation: `${fadeIn} 1s ease-in-out`,
    animationFillMode: "forwards",
  },

  ".fade-in-from-left": {
    opacity: 0,
    animation: `${fadeInFromLeft} 1s ease-in-out`,
    animationFillMode: "forwards",
  },

  ".fade-in-from-right": {
    opacity: 0,
    animation: `${fadeInFromRight} 1s ease-in-out`,
    animationFillMode: "forwards",
  },

  ".animation-delay-0.5": {
    animationDelay: "0.5s",
  },
  ".animation-delay-1": {
    animationDelay: "1s",
  },

  "*": {
    "-webkit-user-select": "none",
    "-ms-user-select": "none",
    "user-select": "none",
  },

  ".desktop": {
    display: "flex",
  },

  gridTemplateAreas: `
    'start-profiting video-container'
		'tags tags'
		'description cards'`,

  ".mobile-header": {
    display: "none",
    justifyContent: "space-between",
    alignItems: "center",

    height: "2.75rem",
    padding: "0 3rem",
    marginBottom: "0.375rem",

    fontWeight: "700",
    fontSize: "1rem",
    lineHeight: "1.5rem",
  },

  ".start-profiting": {
    display: "flex",
    flexDirection: "column",
    gap: 20,
    gridArea: "start-profiting",
    width: "100%",
  },

  ".video-container": {
    position: "relative",
    gridArea: "video-container",
    height: "18.75rem",

    img: {
      objectFit: "cover",
      borderRadius: "$sm",
    },
  },

  ".tags": {
    position: "relative",
    gridArea: "tags",

    borderTop: "1px solid $neutral20",
    borderBottom: "1px solid $neutral20",

    height: "30",

    overflowX: "hidden",

    ".fade-left, .fade-right": {
      position: "absolute",
      width: "5%",
      height: "100%",
      top: 0,
      zIndex: 2,
    },

    ".fade-left": {
      left: -2,
      background: "$fadetoright",
      transform: "translateX(-0.125rem)",
    },

    ".fade-right": {
      right: 1,
      background: "$fadetoleft",
      transform: "translateX(0.125rem)",
    },
  },

  ".description": {
    gridArea: "description",

    display: "flex",
    flexDirection: "column",
    gap: "$24px",
  },

  ".cards": {
    gridArea: "cards",
    display: "grid",
    gridGap: "1.5rem",
    gridTemplateColumns: "1fr 1fr",
  },

  ".card-container": {
    display: "flex",
    flexDirection: "column",

    width: "100%",
    minHeight: "$210px",
    padding: "$24px",
    border: "1px solid $neutral20",
    borderRadius: "$md",

    transition: "border 0.5s ease-in-out",

    "&:hover": {
      border: "1px solid $blue40",
      boxShadow: "$info",
    },
  },

  ".card-title": {
    marginBottom: "1rem",

    fontWeight: 700,
    fontSize: "$18px",
    lineHeight: "$24px",
    color: "$neutral100",
  },

  ".card-icon": {
    width: "1.5rem",
    height: "1.5rem",

    svg: {
      color: "$blue40",
    },
    marginBottom: "1rem",
  },

  ".card-description": {
    fontWeight: 400,
    fontSize: "$16px",
    lineHeight: "24px",

    margin: 0,
    color: "$neutral100",
  },

  ".title, .subtitle, .text, p": {
    margin: 0,

    fontStyle: "normal",
    color: "$neutral90",
  },

  h1: {
    fontWeight: 700,
    fontSize: "$40px",
    lineHeight: "60px",
  },

  h2: {
    fontWeight: 700,
    fontSize: "$32px",
    lineHeight: "48px",
  },

  ".details": {
    color: "$neutral80",
    fontWeight: 400,
    fontSize: "$12px",
    lineHeight: "$16px",
  },

  ".subtitle": {
    fontWeight: 500,
    fontSize: "1rem",
    lineHeight: "1.5rem",
  },

  ".gray-text": {
    background: "$neutral30",
    height: "3.625rem",
  },

  ".divider": {
    background: "$neutral20",
    minHeight: "0.25rem",
    width: "12.5rem",
  },

  ".separator": {
    background: "$neutral20",
    minWidth: "0.0625rem",
    height: "2rem",
  },

  ".text": {
    fontWeight: 400,
    fontSize: "1rem",
    lineHeight: "1.5rem",
  },

  ".start-button": {
    width: "15.5rem",
    height: "3rem",
  },

  a: {
    color: "$primary50",
  },

  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",

    width: "100%",

    p: {
      margin: 0,
    },
    span: {
      display: "flex",
      alignItems: "center",
      gap: 22,
    },
  },

  ".blog-anchor": {
    color: "$neutral80",
    textDecoration: "none",
  },

  svg: {
    color: "$neutral100",
  },

  ".footer.mobile": {
    gridArea: "footer",
  },

  ".cards-mobile": {
    display: "none",
  },

  ".mobile": {
    display: "none",
  },

  "@lg": {
    gridTemplateColumns: "1fr",

    gridTemplateAreas: `
    'start-profiting'
    'video-container'
    'tags'
    'description'
    'cards'
    'footer'`,

    ".start-button": {
      width: "100%",
    },

    ".desktop": {
      display: "none",
    },

    ".mobile": {
      display: "flex",
    },

    ".mobile-header": {
      display: "flex",
    },

    ".video-container": {
      height: "12.75rem",
    },

    "footer.mobile": {
      flexDirection: "column-reverse",
      gap: 8,
    },
  },

  "@md": {
    ".cards-desktop": {
      display: "none",
    },

    ".cards-mobile": {
      display: "grid",
    },
  },

  "@sm": {
    padding: "0.5rem 0 3rem",
    gridRowGap: "2.5rem",

    ".start-profiting": {
      gap: 10,
    },

    ".subtitle": {
      fontSize: "$14px",
    },

    ".text": {
      fontSize: "$14px",
    },

    h1: {
      fontSize: "$20px",
      lineHeight: "$32px",
    },
  },
});
